import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'

interface SuggestionFieldColumnProps {
  fieldLabel: string
  current: string
  suggestion: string | null | undefined
  multiline?: boolean
  rows?: number
  onApply: () => void
  disabled?: boolean
}

export function SuggestionFieldColumn({
  fieldLabel,
  current,
  suggestion,
  multiline = false,
  rows = 4,
  onApply,
  disabled = false,
}: SuggestionFieldColumnProps) {
  const { t } = useTranslation()
  const hasSuggestion = suggestion != null && suggestion.length > 0
  const isIdentical = hasSuggestion && (suggestion ?? '') === current
  const buttonDisabled = disabled || !hasSuggestion || isIdentical

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-start">
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
          {t('productContent.channels.suggestion.currentLabel')}
        </div>
        <div className="text-sm px-3 py-2 rounded-md bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 whitespace-pre-wrap text-neutral-700 dark:text-neutral-300 min-h-[3rem]">
          {current || <span className="text-neutral-400 italic">—</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={onApply}
        disabled={buttonDisabled}
        title={isIdentical ? t('productContent.channels.suggestion.identical') : undefined}
        aria-label={t('productContent.channels.suggestion.applyAria', { field: fieldLabel })}
        className="flex items-center gap-1.5 px-3 py-2 mt-5 bg-orange-500 text-white text-sm font-medium rounded-lg shadow-sm transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange-600"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('productContent.channels.suggestion.apply')}
      </button>
      <div>
        <div className="text-xs uppercase tracking-wider text-orange-600 mb-1">
          {t('productContent.channels.suggestion.label')}
        </div>
        <div
          className={`text-sm px-3 py-2 rounded-md border whitespace-pre-wrap ${
            hasSuggestion
              ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800 text-neutral-900 dark:text-orange-100'
              : 'bg-neutral-50 border-dashed border-neutral-300 dark:border-neutral-700 text-neutral-400 italic'
          } min-h-[3rem]`}
          style={multiline ? { minHeight: `${rows}rem` } : undefined}
        >
          {hasSuggestion ? suggestion : '—'}
        </div>
      </div>
    </div>
  );
}
